import React, { useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, LifeBuoy, Ticket } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { AdminKBEditorPanel } from './AdminKBEditorPanel';
import { AdminSupportTicketsPanel } from './AdminSupportTicketsPanel';

/**
 * AdminHelpCenterPage — the unified Help Center shell.
 *
 * Two tabs:
 *   • Knowledge Base Editor — author / publish / manage SupportArticles.
 *   • Support Ticket Center — global cross-tenant ticket triage with a
 *     live 6-hour SLA countdown and an admin_notes side drawer.
 *
 * Surfaced from the left sidebar of the SuperAdmin dashboard (no longer
 * tucked away inside the Settings popover).
 *
 * The tab selection is mirrored to ?tab=kb|tickets in the URL so admins
 * can deep-link straight to either pane.
 */
export function AdminHelpCenterPage() {
  const { admin, loading } = useAdminAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') === 'tickets' ? 'tickets' : 'kb';
  const [tab, setTab] = useState(initialTab);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const switchTab = (next) => {
    setTab(next);
    const np = new URLSearchParams(params);
    np.set('tab', next);
    setParams(np, { replace: true });
  };

  return (
    <div
      data-testid="admin-help-center-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/admin"
              data-testid="admin-help-back"
              className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </Link>
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <LifeBuoy className="w-4 h-4 text-amber-400" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-semibold">Help Center</div>
              <div className="text-xs text-slate-400">Knowledge base authoring + SLA-aware ticket triage</div>
            </div>
          </div>
          {/* Tabs */}
          <nav className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-xl p-1">
            <button
              onClick={() => switchTab('kb')}
              data-testid="admin-help-tab-kb"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${tab === 'kb'
                ? 'bg-amber-500 text-slate-900 font-semibold'
                : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <BookOpen className="w-3.5 h-3.5" /> Knowledge Base
            </button>
            <button
              onClick={() => switchTab('tickets')}
              data-testid="admin-help-tab-tickets"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${tab === 'tickets'
                ? 'bg-amber-500 text-slate-900 font-semibold'
                : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <Ticket className="w-3.5 h-3.5" /> Support Tickets
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {tab === 'kb' ? <AdminKBEditorPanel /> : <AdminSupportTicketsPanel />}
      </main>
    </div>
  );
}

export default AdminHelpCenterPage;
